import { useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar,
  CreditCard,
  DollarSign,
  RefreshCw,
  Plus,
  Minus,
  Upload,
  Image as ImageIcon,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';

export function SubscriptionManager({ tenant, plans, authHeaders, onUpdate }) {
  const [extendTrialDays, setExtendTrialDays] = useState(14);
  const [selectedPlan, setSelectedPlan] = useState(tenant?.plan_id || '');
  const [newStatus, setNewStatus] = useState(tenant?.subscription_status || 'trialing');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);

  const handleExtendTrial = async () => {
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/extend-trial`, 
        { days: extendTrialDays },
        { headers: authHeaders }
      );
      toast.success(`Trial extended by ${extendTrialDays} days`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const handleResetTrial = async () => {
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/reset-trial?days=14`, {},
        { headers: authHeaders }
      );
      toast.success('Trial reset to 14 days from today');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset trial');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/change-plan`, 
        { plan_id: selectedPlan, subscription_status: newStatus, apply_plan_limits: true },
        { headers: authHeaders }
      );
      toast.success('Plan changed successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/activate`, {},
        { headers: authHeaders }
      );
      toast.success('Subscription activated');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (immediate = false) => {
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/cancel?immediate=${immediate}`, {},
        { headers: authHeaders }
      );
      toast.success(`Subscription canceled ${immediate ? 'immediately' : 'at period end'}`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/reactivate`, {},
        { headers: authHeaders }
      );
      toast.success('Subscription reactivated');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredit = async () => {
    if (!creditAmount || !creditReason) return;
    setLoading(true);
    try {
      await api.post(`/super-admin/subscriptions/tenants/${tenant.id}/add-credit`, 
        { amount: parseFloat(creditAmount), reason: creditReason },
        { headers: authHeaders }
      );
      toast.success(`$${creditAmount} credit added`);
      setCreditAmount('');
      setCreditReason('');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credit');
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const openDialog = (action) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const trialEndsAt = tenant?.trial_ends_at ? new Date(tenant.trial_ends_at) : null;
  const daysLeft = trialEndsAt ? Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className="p-4 bg-slate-700/50 rounded-lg">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-400">Status</p>
            <Badge className={`mt-1 ${
              tenant?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
              tenant?.subscription_status === 'trialing' ? 'bg-blue-500/20 text-blue-400' :
              tenant?.subscription_status === 'canceled' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>
              {tenant?.subscription_status}
            </Badge>
          </div>
          <div>
            <p className="text-xs text-slate-400">Current Plan</p>
            <p className="text-white font-medium">{plans.find(p => p.id === tenant?.plan_id)?.name || 'Free'}</p>
          </div>
          {tenant?.subscription_status === 'trialing' && (
            <>
              <div>
                <p className="text-xs text-slate-400">Trial Ends</p>
                <p className="text-white">{trialEndsAt?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Days Left</p>
                <p className={`font-medium ${daysLeft < 7 ? 'text-red-400' : 'text-green-400'}`}>{daysLeft} days</p>
              </div>
            </>
          )}
          <div>
            <p className="text-xs text-slate-400">Account Credit</p>
            <p className="text-white font-medium">${tenant?.account_credit || 0}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-slate-300">Quick Actions</p>
        <div className="flex flex-wrap gap-2">
          {tenant?.subscription_status === 'trialing' && (
            <>
              <Button size="sm" variant="outline" onClick={() => openDialog('extend')} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                <Plus className="h-3 w-3 mr-1" />
                Extend Trial
              </Button>
              <Button size="sm" variant="outline" onClick={handleResetTrial} className="border-slate-600 text-slate-300">
                <RefreshCw className="h-3 w-3 mr-1" />
                Reset Trial
              </Button>
            </>
          )}
          {tenant?.subscription_status !== 'active' && (
            <Button size="sm" onClick={handleActivate} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-3 w-3 mr-1" />
              Activate
            </Button>
          )}
          {tenant?.subscription_status === 'canceled' && (
            <Button size="sm" variant="outline" onClick={handleReactivate} className="border-green-500/30 text-green-400">
              <RefreshCw className="h-3 w-3 mr-1" />
              Reactivate
            </Button>
          )}
          {tenant?.subscription_status !== 'canceled' && (
            <Button size="sm" variant="outline" onClick={() => openDialog('cancel')} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <XCircle className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Change Plan */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-slate-300">Change Plan</p>
        <div className="flex gap-2">
          <Select value={selectedPlan} onValueChange={setSelectedPlan}>
            <SelectTrigger className="flex-1 bg-slate-800 border-slate-600 text-white">
              <SelectValue placeholder="Select plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.name} - ${plan.price_monthly_usd}/mo
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trialing">Trialing</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="past_due">Past Due</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => openDialog('changePlan')} disabled={!selectedPlan} className="bg-blue-600 hover:bg-blue-700">
            Apply
          </Button>
        </div>
      </div>

      {/* Add Credit */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-slate-300">Add Account Credit</p>
        <Button size="sm" variant="outline" onClick={() => openDialog('credit')} className="border-green-500/30 text-green-400">
          <DollarSign className="h-3 w-3 mr-1" />
          Add Credit
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {dialogAction === 'extend' && 'Extend Trial Period'}
              {dialogAction === 'changePlan' && 'Change Subscription Plan'}
              {dialogAction === 'cancel' && 'Cancel Subscription'}
              {dialogAction === 'credit' && 'Add Account Credit'}
            </DialogTitle>
          </DialogHeader>
          
          {dialogAction === 'extend' && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400">Days to Extend</Label>
                <Input
                  type="number"
                  value={extendTrialDays}
                  onChange={(e) => setExtendTrialDays(parseInt(e.target.value) || 0)}
                  min="1"
                  max="365"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <p className="text-sm text-slate-400">
                Current trial ends: {trialEndsAt?.toLocaleDateString() || 'N/A'}
              </p>
            </div>
          )}

          {dialogAction === 'changePlan' && (
            <div className="space-y-4">
              <p className="text-slate-400">
                Change to <span className="text-white font-medium">{plans.find(p => p.id === selectedPlan)?.name}</span> with status <span className="text-white font-medium">{newStatus}</span>?
              </p>
              <p className="text-sm text-yellow-400">
                This will update the tenant's limits to match the new plan.
              </p>
            </div>
          )}

          {dialogAction === 'cancel' && (
            <div className="space-y-4">
              <p className="text-slate-400">How would you like to cancel?</p>
              <div className="flex gap-2">
                <Button onClick={() => handleCancel(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300">
                  At Period End
                </Button>
                <Button onClick={() => handleCancel(true)} variant="destructive" className="flex-1">
                  Immediately
                </Button>
              </div>
            </div>
          )}

          {dialogAction === 'credit' && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400">Amount ($)</Label>
                <Input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400">Reason</Label>
                <Textarea
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="Reason for adding credit..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            {dialogAction === 'extend' && (
              <Button onClick={handleExtendTrial} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Extend Trial'}
              </Button>
            )}
            {dialogAction === 'changePlan' && (
              <Button onClick={handleChangePlan} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change Plan'}
              </Button>
            )}
            {dialogAction === 'credit' && (
              <Button onClick={handleAddCredit} disabled={loading || !creditAmount || !creditReason} className="bg-green-600 hover:bg-green-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Credit'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LogoUploader({ tenantId, currentLogoUrl, authHeaders, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentLogoUrl || null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, GIF, WebP, or SVG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', tenantId);

      const response = await api.post('/uploads/logo', formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully');
      
      // Update tenant branding with new logo URL
      await api.put(`/super-admin/tenants/${tenantId}/branding`, {
        logo_url: response.data.logo_url
      }, { headers: authHeaders });

      onUpdate(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setPreview(currentLogoUrl);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-slate-400">Company Logo</Label>
      
      {/* Preview */}
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-600">
          {preview ? (
            <img src={preview} alt="Logo preview" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="h-8 w-8 text-slate-500" />
          )}
        </div>
        
        <div className="space-y-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-slate-600 text-slate-300"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload Logo</>
            )}
          </Button>
          <p className="text-xs text-slate-500">
            PNG, JPG, GIF, WebP, or SVG. Max 5MB.
          </p>
          <p className="text-xs text-slate-500">
            Will be auto-resized to multiple sizes (256px, 128px, 64px, 32px)
          </p>
        </div>
      </div>
    </div>
  );
}
